import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { FedexExpectedDeliveryOutDTO, FedexExpeditionTrackingOutDTO } from '@api/ringil-api.v2';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';
import dayjs from 'dayjs';

interface ComponentProps {
  expeditionId: string;
}

// FedEx timestamps carry the offset of the delivery address - show them as FedEx does
// (local wall-clock time there), not converted to the viewer's zone. A midnight value is a
// date-only commitment, so the time is left out.
const localDate = (iso: string) => iso.substring(0, 10);
const localTime = (iso: string) => iso.substring(11, 16);

const formatExpectedDelivery = (expected?: FedexExpectedDeliveryOutDTO): string | null => {
  if (!expected) return null;
  const { begins, ends } = expected;
  if (begins && ends) {
    return localDate(begins) === localDate(ends)
      ? `${localDate(begins)} ${localTime(begins)}–${localTime(ends)}`
      : `${localDate(begins)} ${localTime(begins)} – ${localDate(ends)} ${localTime(ends)}`;
  }
  const value = ends ?? begins;
  if (!value) return null;
  const time = localTime(value);
  return time && time !== '00:00' ? `${localDate(value)} ${time}` : localDate(value);
};

/**
 * Live FedEx tracking for an expedition detail: tracking number, latest status,
 * current delivery estimate and scan-event history. Renders nothing when the expedition's carrier is not
 * FedEx or no shipment exists yet (the endpoint answers with a validation
 * error, which simply hides the panel).
 */
const FedexTrackingPanel: React.FC<ComponentProps> = ({ expeditionId }) => {
  const { tr } = useTranslationLgs();
  const { getFedexExpeditionTracking } = useContext(ApiContext);
  const [tracking, setTracking] = useState<FedexExpeditionTrackingOutDTO | null>(null);

  useEffect(() => {
    if (!expeditionId) return;
    getFedexExpeditionTracking(
      expeditionId,
      data => setTracking(data),
      null,
      {
        onValidationFailed: () => setTracking(null),
        onError: () => setTracking(null),
      },
    );
  }, [expeditionId]);

  if (tracking == null) return null;

  // FedEx sends the estimate per shipment (refreshed on every load), not per scan event
  const isDelivered = tracking.latestStatusCode === 'DL';
  const expectedDelivery = formatExpectedDelivery(tracking.expectedDelivery);

  return (
    <div className="flex flex-col gap-3 mt-4 p-4 rounded-lg bg-base-tertiary">
      <div className="flex flex-row items-center gap-3">
        <FedexLogo />
        <COHeadline variant={'h4'} title={tr('FedexTrackingPanel.title', 'FedEx tracking')} />
      </div>
      <div className="flex flex-row gap-8 flex-wrap">
        <div>
          <div className="text-content-tertiary text-sm">{tr('FedexTrackingPanel.trackingNumber', 'Tracking number')}</div>
          <div
            className="font-medium cursor-pointer"
            title={tr('FedexTrackingPanel.copy', 'Click to copy')}
            onClick={() => navigator.clipboard?.writeText(tracking.trackingNumber)}
          >
            {tracking.trackingNumber}
          </div>
        </div>
        {tracking.accountNumber && (
          <div>
            <div className="text-content-tertiary text-sm">{tr('FedexTrackingPanel.account', 'FedEx account')}</div>
            <div className="font-medium">{tracking.accountNumber}</div>
          </div>
        )}
        {tracking.latestStatusDescription && (
          <div>
            <div className="text-content-tertiary text-sm">{tr('FedexTrackingPanel.latestStatus', 'Latest status')}</div>
            <div className="font-medium">
              {tracking.latestStatusDescription}
              {tracking.latestStatusCode ? ` (${tracking.latestStatusCode})` : ''}
            </div>
          </div>
        )}
        {!isDelivered && (expectedDelivery || tracking.serviceCommitMessage) && (
          <div>
            <div className="text-content-tertiary text-sm">{tr('FedexTrackingPanel.estimatedDelivery', 'Expected delivery')}</div>
            <div className="font-medium">{expectedDelivery ?? tracking.serviceCommitMessage}</div>
          </div>
        )}
      </div>

      {tracking.scanEvents?.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-content-tertiary text-sm">{tr('FedexTrackingPanel.history', 'History')}</div>
          {tracking.scanEvents.map((event, index) => (
            <div key={index} className="flex flex-row gap-3 text-sm">
              <div className="text-content-tertiary w-[150px] shrink-0">
                {event.date ? dayjs(event.date).format('YYYY-MM-DD HH:mm') : ''}
              </div>
              <div>
                {event.eventDescription ?? event.derivedStatus}
                {event.exceptionDescription && (
                  <span className="text-red-700"> — {event.exceptionDescription}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <FedexDisclaimer />
    </div>
  );
};

export default FedexTrackingPanel;
